import type Phaser from 'phaser';
import type { BiomeId } from '../../content/biomes';
import { TILE_SIZE } from '../layout';

/**
 * The world outside the fence (docs/ART_DIRECTION.md: surroundings recede). One static drawing
 * behind the lawn, filling a wide margin on every side so small lawns sit in a place instead of
 * empty space. Muted, darker and low in detail so it never competes with the lawn, and never
 * part of the sim. Deterministic per lawn (seeded by its id).
 */
const MARGIN = TILE_SIZE * 14;
const DEPTH = -20;

const MUTED = {
  ground: 0x2b4a26,
  groundDark: 0x253f21,
  ink: 0x10180c,
  siding: 0x7d735e,
  sidingLine: 0x6f6653,
  trim: 0xa39a85,
  roof: 0x5a3a32,
  window: 0x4f7894,
  windowGlint: 0x7fa3bc,
  door: 0x7a4434,
  stone: 0x736b5d,
  stoneLine: 0x625b4f,
  soil: 0x4e3622,
  shrub: 0x244a22,
  shrubLight: 0x33602e,
  flowers: [0xb5607a, 0xb8a24a, 0xb7b3a8, 0x8f78b0],
  gravel: 0x7f765f,
  gravelLine: 0x6c6451,
  trunk: 0x5a4430,
  canopy: 0x2a5527,
  canopyLight: 0x3a6c34,
  bench: 0x7a5a3a,
  lamp: 0x5d6369,
  barn: 0x7a3a2e,
  barnLine: 0x652f26,
  crop: 0x6b7a3a,
  cropLine: 0x58662f,
  dirt: 0x6e5236,
  hay: 0xa08a4a,
  fairway: 0x2f5a2a,
  fairwayLine: 0x335f2d,
  sand: 0x8f8461,
  flag: 0x9a4a3c,
  wall: 0x5f5a54,
  wallLine: 0x4c4843,
  iron: 0x23252b,
  yew: 0x1c3a1f,
  yewLight: 0x264a28,
  grave: 0x6e6f73,
  graveLight: 0x85868a,
};

/** Small deterministic hash: the same lawn always gets the same scenery. */
function hash(seed: number, a: number, b: number): number {
  let h = Math.imul(seed ^ (a * 374761393) ^ (b * 668265263), 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1103515245);
  return (h ^ (h >>> 16)) >>> 0;
}

function seedOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h >>> 0;
}

/**
 * Draws the surroundings. Most of it lies behind the lawn's fence; what stands on the near side
 * of the bottom fence and rises up past it (the Park's lamp posts) is nearer the viewer, so it is
 * drawn on a second layer at `frontDepth`, in front of the fence.
 */
export function drawSurroundings(
  scene: Phaser.Scene,
  levelId: string,
  biome: BiomeId,
  w: number,
  h: number,
  frontDepth: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics().setDepth(DEPTH);
  const front = scene.add.graphics().setDepth(frontDepth);
  const seed = seedOf(levelId);
  const left = -MARGIN;
  const right = w + MARGIN;
  const top = -MARGIN;
  const bottom = h + MARGIN;

  // Ground with sparse darker patches (no tufts: tufts mean "mow me").
  g.fillStyle(MUTED.ground).fillRect(left, top, right - left, bottom - top);
  for (let y = top; y < bottom; y += 8) {
    for (let x = left; x < right; x += 8) {
      if (hash(seed, x, y) % 9 === 0) g.fillStyle(MUTED.groundDark).fillRect(x, y, 4, 2);
    }
  }

  switch (biome) {
    case 'farm':
      drawFarm(g, seed, w, h, left, right, top, bottom);
      break;
    case 'golf':
      drawGolf(g, seed, w, h, left, right, top, bottom);
      break;
    case 'park':
      drawPark(g, front, seed, w, h, left, right, top, bottom);
      break;
    case 'cemetery':
      drawCemetery(g, seed, w, h, left, right, top, bottom);
      break;
    case 'estate':
      drawEstate(g, seed, w, h, left, right, top, bottom);
      break;
    case 'stadium':
      drawStadium(g, seed, w, h, left, right, top, bottom);
      break;
    case 'meadow':
      drawMeadow(g, seed, w, h, left, right, top, bottom);
      break;
    default:
      drawBackyard(g, seed, w, h, left, right, top, bottom);
  }
  return g;
}

/** The back of the house above, flower beds and shrubs at the sides, a stone path below. */
function drawBackyard(
  g: Phaser.GameObjects.Graphics,
  seed: number,
  w: number,
  h: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): void {
  // House wall: siding from the top of the margin down to just above the fence.
  const wallBottom = -14;
  g.fillStyle(MUTED.siding).fillRect(left, top, right - left, wallBottom - top);
  for (let y = wallBottom - 4; y > top; y -= 4)
    g.fillStyle(MUTED.sidingLine).fillRect(left, y, right - left, 1);
  g.fillStyle(MUTED.trim).fillRect(left, wallBottom - 2, right - left, 2);
  // Windows and a back door with steps, spaced along the wall.
  const doorX = Math.round(w / 2 + ((hash(seed, 1, 2) % 5) - 2) * TILE_SIZE) - 10;
  for (let x = left + 12; x < right - 30; x += 52) {
    if (Math.abs(x + 12 - (doorX + 10)) < 36) continue;
    window(g, x, wallBottom - 38);
  }
  g.fillStyle(MUTED.ink).fillRect(doorX - 1, wallBottom - 33, 22, 31);
  g.fillStyle(MUTED.door).fillRect(doorX, wallBottom - 32, 20, 30);
  g.fillStyle(MUTED.trim).fillRect(doorX + 15, wallBottom - 18, 2, 2);
  g.fillStyle(MUTED.stone).fillRect(doorX - 6, wallBottom, 32, 4);
  g.fillStyle(MUTED.stoneLine).fillRect(doorX - 6, wallBottom + 3, 32, 1);

  // Flower beds hugging the side fences, shrubs beyond.
  for (const side of [-1, 1]) {
    const bedX = side < 0 ? -34 : w + 10;
    g.fillStyle(MUTED.soil).fillRect(bedX, -8, 24, h + 16);
    for (let y = -4; y < h + 6; y += 7) {
      const c = MUTED.flowers[hash(seed, bedX, y) % MUTED.flowers.length]!;
      g.fillStyle(c).fillRect(bedX + 4 + (hash(seed, y, bedX) % 14), y, 3, 3);
    }
    for (let y = 0; y < h; y += 40) {
      shrub(g, side < 0 ? bedX - 22 : bedX + 30, y + (hash(seed, side, y) % 16), 9);
    }
  }

  // Stone path below the fence.
  const pathY = h + 14;
  g.fillStyle(MUTED.stone).fillRect(left, pathY, right - left, 14);
  for (let x = left; x < right; x += 16)
    g.fillStyle(MUTED.stoneLine).fillRect(x + ((pathY / 16) % 2) * 8, pathY, 1, 14);
  g.fillStyle(MUTED.stoneLine).fillRect(left, pathY + 7, right - left, 1);
  for (let x = left + 10; x < right; x += 60) shrub(g, x + (hash(seed, x, 9) % 20), pathY + 34, 10);
  void bottom;
}

/** Gravel paths, trees, benches and lamp posts. */
function drawPark(
  g: Phaser.GameObjects.Graphics,
  front: Phaser.GameObjects.Graphics,
  seed: number,
  w: number,
  h: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): void {
  for (const y of [-34, h + 14]) {
    g.fillStyle(MUTED.gravel).fillRect(left, y, right - left, 14);
    g.fillStyle(MUTED.gravelLine).fillRect(left, y, right - left, 1);
    g.fillStyle(MUTED.gravelLine).fillRect(left, y + 13, right - left, 1);
    for (let x = left + 20; x < right; x += 70) {
      const kind = hash(seed, x, y) % 3;
      if (kind === 0) bench(g, x, y + 2);
      // A lamp stands on the path's near edge; on the near path it is in front of the fence.
      else if (kind === 1) lamp(y > h ? front : g, x, y - 2);
    }
  }
  for (let i = 0; i < 26; i++) {
    const x = left + (hash(seed, i, 11) % (right - left));
    const y = top + (hash(seed, i, 17) % (bottom - top));
    const nearLawn = x > -40 && x < w + 40 && y > -48 && y < h + 40;
    if (!nearLawn) tree(g, x, y);
  }
}

/**
 * The old grounds: a stone wall above with an iron gate, dark yews standing along the sides, more
 * rows of headstones beyond, and a gravel path below.
 */
function drawCemetery(
  g: Phaser.GameObjects.Graphics,
  seed: number,
  w: number,
  h: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): void {
  // Headstones in rows across the whole margin (the lawn covers its own patch).
  for (let y = top + 10; y < bottom; y += 26) {
    for (let x = left + 8 + ((y / 26) % 2) * 9; x < right; x += 18) {
      const nearLawn = x > -24 && x < w + 16 && y > -48 && y < h + 30;
      if (nearLawn || hash(seed, x, y) % 5 === 0) continue;
      g.fillStyle(MUTED.ink).fillRect(x - 1, y - 1, 9, 12);
      g.fillStyle(MUTED.grave).fillRect(x, y, 7, 10);
      g.fillStyle(MUTED.graveLight).fillRect(x, y, 3, 10);
      g.fillStyle(MUTED.ground)
        .fillRect(x, y, 1, 1)
        .fillRect(x + 6, y, 1, 1);
    }
  }
  // The wall and its gate, centered above the lawn.
  const wallBottom = -16;
  g.fillStyle(MUTED.wall).fillRect(left, wallBottom - 18, right - left, 18);
  for (let y = wallBottom - 18; y < wallBottom; y += 6) {
    g.fillStyle(MUTED.wallLine).fillRect(left, y, right - left, 1);
    for (let x = left + ((y / 6) % 2) * 6; x < right; x += 12)
      g.fillStyle(MUTED.wallLine).fillRect(x, y, 1, 6);
  }
  const gx = Math.round(w / 2);
  g.fillStyle(MUTED.ground).fillRect(gx - 14, wallBottom - 18, 28, 18);
  g.fillStyle(MUTED.iron);
  for (let x = gx - 13; x <= gx + 12; x += 4) g.fillRect(x, wallBottom - 22, 1, 22);
  g.fillRect(gx - 14, wallBottom - 16, 28, 1).fillRect(gx - 14, wallBottom - 6, 28, 1);
  // Yews down both sides.
  for (const side of [-1, 1]) {
    for (let y = 0; y < h; y += 34) {
      const x = side < 0 ? -26 : w + 26;
      const ty = y + (hash(seed, side, y) % 10);
      g.fillStyle(MUTED.ink).fillEllipse(x, ty + 12, 18, 30);
      g.fillStyle(MUTED.yew).fillEllipse(x, ty + 12, 16, 28);
      g.fillStyle(MUTED.yewLight).fillEllipse(x - 3, ty + 6, 6, 14);
    }
  }
  // A gravel path below the fence.
  const pathY = h + 14;
  g.fillStyle(MUTED.gravel).fillRect(left, pathY, right - left, 12);
  g.fillStyle(MUTED.gravelLine).fillRect(left, pathY, right - left, 1);
  g.fillStyle(MUTED.gravelLine).fillRect(left, pathY + 11, right - left, 1);
}

/**
 * The manor: its long stone facade above, tall windows and a grand door on the middle, clipped
 * hedges and urns down the sides, and a raked gravel drive below.
 */
function drawEstate(
  g: Phaser.GameObjects.Graphics,
  seed: number,
  w: number,
  h: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): void {
  const wallBottom = -14;
  g.fillStyle(MUTED.stone).fillRect(left, top, right - left, wallBottom - top);
  for (let y = wallBottom - 8; y > top; y -= 8)
    g.fillStyle(MUTED.stoneLine).fillRect(left, y, right - left, 1);
  g.fillStyle(MUTED.trim).fillRect(left, wallBottom - 3, right - left, 3);
  const cx = Math.round(w / 2);
  for (let x = cx - 12 - 40 * 6; x < right; x += 40) {
    if (Math.abs(x + 6 - cx) < 20) continue;
    g.fillStyle(MUTED.ink).fillRect(x - 1, wallBottom - 44, 14, 32);
    g.fillStyle(MUTED.window).fillRect(x, wallBottom - 43, 12, 30);
    g.fillStyle(MUTED.trim).fillRect(x + 5, wallBottom - 43, 2, 30);
    g.fillStyle(MUTED.windowGlint).fillRect(x + 1, wallBottom - 42, 3, 4);
  }
  // The grand door, with columns.
  g.fillStyle(MUTED.ink).fillRect(cx - 11, wallBottom - 40, 22, 40);
  g.fillStyle(MUTED.door).fillRect(cx - 9, wallBottom - 38, 18, 38);
  g.fillStyle(MUTED.trim)
    .fillRect(cx - 16, wallBottom - 44, 4, 44)
    .fillRect(cx + 12, wallBottom - 44, 4, 44);
  // Clipped hedges and urns down both sides.
  for (const side of [-1, 1]) {
    const x0 = side < 0 ? left : w + 10;
    const x1 = side < 0 ? -10 : right;
    g.fillStyle(MUTED.ink).fillRect(x0, -8, x1 - x0, h + 16);
    g.fillStyle(MUTED.shrub).fillRect(x0, -7, x1 - x0, h + 14);
    g.fillStyle(MUTED.shrubLight).fillRect(x0, -7, x1 - x0, 3);
    for (let y = 8; y < h; y += 48) {
      const ux = side < 0 ? -20 : w + 20;
      g.fillStyle(MUTED.ink).fillCircle(ux, y + (hash(seed, side, y) % 6), 5);
      g.fillStyle(MUTED.gravel).fillCircle(ux, y + (hash(seed, side, y) % 6), 4);
    }
  }
  // The gravel drive below.
  const driveY = h + 14;
  g.fillStyle(MUTED.gravel).fillRect(left, driveY, right - left, 20);
  for (let x = left; x < right; x += 3)
    g.fillStyle(MUTED.gravelLine).fillRect(x, driveY + 2 + (x % 7), 1, 1);
  void bottom;
}

/**
 * The stadium: banked stands of fans all round (rows of tiny colored heads), a running track
 * inside them, floodlight towers at the corners and a scoreboard above the far end.
 */
function drawStadium(
  g: Phaser.GameObjects.Graphics,
  seed: number,
  w: number,
  h: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): void {
  // The track round the pitch.
  g.fillStyle(0x6a3a2e).fillRect(-20, -26, w + 40, h + 52);
  g.fillStyle(MUTED.ground).fillRect(-6, -12, w + 12, h + 24);
  // Stands: rows of fans beyond the track.
  const fans = [0x8a4a4a, 0x4a5a8a, 0xa08a4a, 0x7a7a7a, 0x5a7a5a, 0x9a6a8a];
  for (let y = top; y < bottom; y += 4) {
    for (let x = left; x < right; x += 3) {
      const inside = x > -24 && x < w + 22 && y > -30 && y < h + 28;
      if (inside) continue;
      g.fillStyle((y / 4) % 2 === 0 ? 0x3a3550 : 0x332f48).fillRect(x, y, 3, 4);
      if (hash(seed, x, y) % 3 !== 0)
        g.fillStyle(fans[hash(seed, y, x) % fans.length]!).fillRect(x + 1, y + 1, 1, 2);
    }
  }
  // The scoreboard above the far end.
  const sx = Math.round(w / 2) - 30;
  g.fillStyle(MUTED.ink).fillRect(sx - 1, -71, 62, 26);
  g.fillStyle(0x1c2030).fillRect(sx, -70, 60, 24);
  g.fillStyle(0xffd23f)
    .fillRect(sx + 8, -64, 10, 12)
    .fillRect(sx + 42, -64, 10, 12);
  g.fillStyle(0xff8a1f).fillRect(sx + 28, -60, 4, 4);
  // Floodlight towers at the corners.
  for (const [fx, fy] of [
    [-34, -40],
    [w + 26, -40],
    [-34, h + 30],
    [w + 26, h + 30],
  ] as const) {
    g.fillStyle(MUTED.ink).fillRect(fx + 3, fy, 2, 20);
    g.fillStyle(0xf4f1de).fillRect(fx - 1, fy - 6, 10, 6);
    g.fillStyle(0xfff2a8).fillRect(fx, fy - 5, 8, 4);
  }
}

/**
 * Neighboring holes: striped fairways beyond the fence, a bunker or two, a distant flag, trees
 * between the holes and a cart path below.
 */
function drawGolf(
  g: Phaser.GameObjects.Graphics,
  seed: number,
  w: number,
  h: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): void {
  // Fairway stripes across the whole margin (the lawn covers its own patch).
  for (let y = top; y < bottom; y += 16) {
    if (((y - top) / 16) % 2 === 0)
      g.fillStyle(MUTED.fairwayLine).fillRect(left, y, right - left, 16);
    else g.fillStyle(MUTED.fairway).fillRect(left, y, right - left, 16);
  }
  // Bunkers and a far flag.
  for (let i = 0; i < 6; i++) {
    const x = left + (hash(seed, i, 23) % (right - left));
    const y = top + (hash(seed, i, 29) % (bottom - top));
    const nearLawn = x > -40 && x < w + 40 && y > -48 && y < h + 40;
    if (nearLawn) continue;
    g.fillStyle(MUTED.ink).fillEllipse(x, y, 30, 14);
    g.fillStyle(MUTED.sand).fillEllipse(x, y, 28, 12);
  }
  const fx = hash(seed, 3, 5) % 2 === 0 ? -50 : w + 44;
  g.fillStyle(MUTED.trim).fillRect(fx, -40, 1, 18);
  g.fillStyle(MUTED.flag).fillTriangle(fx + 1, -40, fx + 9, -37, fx + 1, -34);
  // Trees between the holes.
  for (let i = 0; i < 18; i++) {
    const x = left + (hash(seed, i, 11) % (right - left));
    const y = top + (hash(seed, i, 17) % (bottom - top));
    const nearLawn = x > -40 && x < w + 40 && y > -48 && y < h + 40;
    if (!nearLawn) tree(g, x, y);
  }
  // Cart path below the fence.
  const pathY = h + 14;
  g.fillStyle(MUTED.gravel).fillRect(left, pathY, right - left, 12);
  g.fillStyle(MUTED.gravelLine).fillRect(left, pathY, right - left, 1);
  g.fillStyle(MUTED.gravelLine).fillRect(left, pathY + 11, right - left, 1);
}

/** The barn wall above, crop rows at the sides, a dirt track and hay bales below. */
function drawFarm(
  g: Phaser.GameObjects.Graphics,
  seed: number,
  w: number,
  h: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): void {
  const wallBottom = -14;
  g.fillStyle(MUTED.barn).fillRect(left, top, right - left, wallBottom - top);
  for (let x = left; x < right; x += 6)
    g.fillStyle(MUTED.barnLine).fillRect(x, top, 1, wallBottom - top);
  g.fillStyle(MUTED.trim).fillRect(left, wallBottom - 2, right - left, 2);
  for (const side of [-1, 1]) {
    const x0 = side < 0 ? left : w + 10;
    const x1 = side < 0 ? -10 : right;
    for (let x = x0; x < x1; x += 6) {
      g.fillStyle(MUTED.crop).fillRect(x, -8, 4, h + 16);
      g.fillStyle(MUTED.cropLine).fillRect(x + 4, -8, 2, h + 16);
    }
  }
  const trackY = h + 14;
  g.fillStyle(MUTED.dirt).fillRect(left, trackY, right - left, 16);
  g.fillStyle(MUTED.soil).fillRect(left, trackY + 4, right - left, 2);
  g.fillStyle(MUTED.soil).fillRect(left, trackY + 10, right - left, 2);
  for (let x = left + 16; x < right; x += 58) hay(g, x + (hash(seed, x, 3) % 18), trackY + 30);
  void bottom;
}

/**
 * Rolling Meadows: open country all round. Wildflowers in the long grass, a dry-stone wall along
 * the top with woods beyond, hedgerows down the sides, and a country lane below the fence.
 */
function drawMeadow(
  g: Phaser.GameObjects.Graphics,
  seed: number,
  w: number,
  h: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): void {
  // Wildflowers dotted through the margins' grass (the lawn covers its own patch).
  for (let y = top; y < bottom; y += 6) {
    for (let x = left; x < right; x += 6) {
      const n = hash(seed, x, y) % 23;
      if (n < 3) g.fillStyle(MUTED.flowers[n]!).fillRect(x + (hash(seed, y, x) % 4), y, 2, 2);
    }
  }
  // A dry-stone wall above the lawn, woods beyond it.
  const wallY = -22;
  for (let x = left; x < right; x += 6) {
    g.fillStyle(MUTED.ink).fillRect(x, wallY, 6, 8);
    g.fillStyle(x % 12 === 0 ? MUTED.stone : MUTED.stoneLine).fillRect(x + 1, wallY + 1, 4, 6);
  }
  for (let i = 0; i < 30; i++) {
    const x = left + (hash(seed, i, 11) % (right - left));
    const y = top + (hash(seed, i, 17) % (wallY - 8 - top));
    tree(g, x, y);
  }
  // Hedgerows down either side.
  for (const [x0, x1] of [
    [left, -12],
    [w + 12, right],
  ] as const) {
    g.fillStyle(MUTED.ink).fillRect(x0, -14, x1 - x0, h + 28);
    g.fillStyle(MUTED.shrub).fillRect(x0 + 1, -13, x1 - x0 - 2, h + 26);
    for (let y = -10; y < h + 10; y += 9) {
      g.fillStyle(MUTED.shrubLight).fillRect(x0 + 2 + (hash(seed, x0, y) % 6), y, 5, 3);
    }
  }
  // A country lane below the fence, and more trees past it.
  const laneY = h + 14;
  g.fillStyle(MUTED.dirt).fillRect(left, laneY, right - left, 14);
  g.fillStyle(MUTED.soil).fillRect(left, laneY + 4, right - left, 2);
  g.fillStyle(MUTED.soil).fillRect(left, laneY + 9, right - left, 2);
  for (let x = left + 20; x < right; x += 64) tree(g, x + (hash(seed, x, 7) % 24), laneY + 40);
}

function window(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  g.fillStyle(MUTED.ink).fillRect(x - 1, y - 1, 26, 22);
  g.fillStyle(MUTED.trim).fillRect(x, y, 24, 20);
  g.fillStyle(MUTED.window).fillRect(x + 2, y + 2, 20, 16);
  g.fillStyle(MUTED.trim).fillRect(x + 11, y + 2, 2, 16);
  g.fillStyle(MUTED.windowGlint).fillRect(x + 3, y + 3, 3, 3);
}

function shrub(g: Phaser.GameObjects.Graphics, x: number, y: number, r: number): void {
  g.fillStyle(MUTED.ink).fillCircle(x, y, r + 1);
  g.fillStyle(MUTED.shrub).fillCircle(x, y, r);
  g.fillStyle(MUTED.shrubLight).fillCircle(x - r / 3, y - r / 3, r / 2);
}

function tree(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  g.fillStyle(MUTED.trunk).fillRect(x - 2, y + 6, 4, 10);
  g.fillStyle(MUTED.ink).fillCircle(x, y, 15);
  g.fillStyle(MUTED.canopy).fillCircle(x, y, 14);
  g.fillStyle(MUTED.canopyLight).fillCircle(x - 4, y - 4, 7);
}

function bench(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  g.fillStyle(MUTED.ink).fillRect(x - 1, y - 1, 26, 10);
  g.fillStyle(MUTED.bench).fillRect(x, y, 24, 3);
  g.fillStyle(MUTED.bench).fillRect(x, y + 5, 24, 3);
}

function lamp(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  g.fillStyle(MUTED.lamp).fillRect(x, y, 2, 16);
  g.fillStyle(MUTED.trim).fillRect(x - 2, y - 3, 6, 4);
}

function hay(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  g.fillStyle(MUTED.ink).fillCircle(x, y, 9);
  g.fillStyle(MUTED.hay).fillCircle(x, y, 8);
  g.fillStyle(MUTED.soil).fillRect(x - 6, y, 12, 1);
}
